/** @jsx jsx */

import type { ButtonHTMLAttributes, HTMLAttributes } from 'react';
import { forwardRef, useMemo } from 'react';
import { jsx } from '@balance-web/core';
import { XCircleIcon } from '@balance-web/icon/icons/XCircleIcon';
import type { InputSizeType } from '@balance-web/text-input';
import {
  Adornment,
  AdornmentWrapper,
  useInputStyles,
} from '@balance-web/text-input';
import { useTheme } from '@balance-web/theme';

type DeprecatedButtonProps = {
  invalid?: boolean;
  isSelected?: boolean;
  size: InputSizeType;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export const InputButton = forwardRef<HTMLButtonElement, DeprecatedButtonProps>(
  ({ invalid = false, isSelected, size, ...props }, ref) => {
    const inputStyles = useInputStyles({
      shape: 'square',
      size: size,
    });
    const focusStyles = isSelected
      ? {
          ...inputStyles[':focus'],
          ':hover': inputStyles[':focus'],
          ':focus': inputStyles[':focus'],
        }
      : null;
    const buttonStyles = {
      ...inputStyles,
      ...focusStyles,
      cursor: 'pointer',
      lineHeight: 'initial', // let the button vertically align its text; the have different native behaviour to inputs
      textAlign: 'left',
    } as const;

    return (
      <div css={{ flex: 1 }}>
        <AdornmentWrapper shape="square" size={size}>
          <button
            ref={ref}
            css={buttonStyles}
            data-invalid={invalid}
            type="button"
            {...props}
          />
        </AdornmentWrapper>
      </div>
    );
  }
);

InputButton.displayName = 'InputButton';

type ClearButtonProps = {
  size: InputSizeType;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export const ClearButton = ({ size, ...props }: ClearButtonProps) => {
  const { semantic, sizing } = useTheme();

  const rightOffset = useMemo(() => {
    if (size === 'small') {
      return sizing.small;
    }
    return sizing.base;
  }, [size, sizing.base, sizing.small]);

  return (
    <Adornment
      align="right"
      as="button"
      css={{
        alignItems: 'center',
        background: 0,
        border: 0,
        borderRadius: '50%',
        color: semantic.colour.text.textTertiary,
        display: 'flex',
        justifyContent: 'center',
        outline: 0,
        padding: 0,
        right: rightOffset,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',

        // No focus styles because this button is not focusable
        ':hover': {
          color: semantic.colour.text.textSecondary,
        },
      }}
      tabIndex={-1}
      type="button"
      {...props}
    >
      <XCircleIcon size={size} />
    </Adornment>
  );
};

export const Placeholder = (props: HTMLAttributes<HTMLSpanElement>) => {
  const { semantic } = useTheme();
  return (
    <span
      css={{
        color: semantic.colour.text.textPlaceholder,
        pointerEvents: 'none',
      }}
      {...props}
    />
  );
};
